import { InputFormatConfig, LanguageIso } from '@mkp/shared/models';
import { format, Locale } from 'date-fns';
import { de, enGB, frCH } from 'date-fns/locale';

export const parseDate = (dateString: string): Date | null => {
  if (!dateString) {
    return null;
  }
  // Check if the format matches DD.MM.YYYY
  const regex = /^(\d{2})\.(\d{2})\.(\d{4})$/;
  const match = dateString.match(regex);
  if (!match) {
    return null;
  }
  // Extract day, month, and year
  const day = parseInt(match[1], 10);
  const month = parseInt(match[2], 10) - 1; // JS months are 0-indexed
  const year = parseInt(match[3], 10);
  // Create a new date object with the parsed values
  const date = new Date(year, month, day);
  // Verify the date is valid (handles cases like 31.02.2023)
  if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) {
    return null;
  }
  return date;
};

export const formatDate = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

export const formatDateTime = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes}`;
};

const DATE_FNS_LOCALES: Record<LanguageIso, Locale> = {
  [LanguageIso.ENGLISH]: enGB,
  [LanguageIso.GERMAN]: de,
  [LanguageIso.FRENCH]: frCH,
};

export const formatDateWithWeekday = (date: Date, lang?: string): string =>
  format(date, 'EEEE, dd.MM.yyyy', { locale: DATE_FNS_LOCALES[lang as LanguageIso] ?? enGB });

export const addYears = (date: Date, years: number): Date => {
  const newDate = new Date(date);
  newDate.setFullYear(newDate.getFullYear() + years);
  return newDate;
};

export const dateFormatter: InputFormatConfig = {
  format: (text: string) => {
    if (!text) return '';

    const sanitized = text.replace(/[^\d.]/g, '');

    if (hasExactlyOnePeriodWithThreeDigitsAfter(sanitized)) {
      return formatSinglePeriodCase(sanitized);
    }

    if (hasTwoOrMorePeriods(sanitized) || hasOnePeriod(sanitized)) {
      return sanitized; // Preserve existing format
    }

    // Fresh input (no periods)
    return formatInputWithoutPeriods(sanitized);
  },

  unformat: (text: string) => (text ? text.replace(/[^\d.]/g, '') : ''),
};

// Helper functions
const hasExactlyOnePeriodWithThreeDigitsAfter = (text: string): boolean => {
  const parts = text.split('.');
  return parts.length === 2 && parts[1].length === 3;
};

const hasTwoOrMorePeriods = (text: string): boolean => (text.match(/\./g) || []).length >= 2;

const hasOnePeriod = (text: string): boolean => (text.match(/\./g) || []).length === 1;

const formatSinglePeriodCase = (text: string): string => {
  const parts = text.split('.');
  return `${parts[0]}.${parts[1].substring(0, 2)}.${parts[1].substring(2)}`;
};

const formatInputWithoutPeriods = (digits: string): string => {
  if (digits.length <= 2) {
    return digits;
  }

  if (digits.length <= 4) {
    return `${digits.substring(0, 2)}.${digits.substring(2)}`;
  }

  return `${digits.substring(0, 2)}.${digits.substring(2, 4)}.${digits.substring(4)}`;
};

/**
 * Returns the absolute difference in whole months between two dates.
 */
export const monthDiff = (date1: Date, date2: Date): number => {
  const years = date2.getFullYear() - date1.getFullYear();
  const months = date2.getMonth() - date1.getMonth();
  return Math.abs(years * 12 + months);
};

const atMidnight = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const getNextMonday = (from: Date = new Date()): Date => {
  const result = atMidnight(from);
  const daysUntilMonday = (8 - result.getDay()) % 7 || 7;
  result.setDate(result.getDate() + daysUntilMonday);
  return result;
};

export const getNextSevenDays = (from: Date = new Date()): Date[] => {
  const base = atMidnight(from);
  return Array.from({ length: 7 }, (_, index) => {
    const day = new Date(base);
    day.setDate(base.getDate() + index + 1);
    return day;
  });
};

export const toScheduleStart = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}T00:00:00+00:00`;
};
